/* eslint-disable @typescript-eslint/no-explicit-any */
import { from, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, take, tap, toArray } from 'rxjs/operators'
import axios, { type AxiosInstance } from 'axios'

const allLicenses = [
  "http://dcat-ap.de/def/licenses/cc-by/4.0",
  "http://dcat-ap.de/def/licenses/cc-zero",
  "http://dcat-ap.de/def/licenses/dl-by-de/2.0",
  "http://dcat-ap.de/def/licenses/cc-by",
  "http://dcat-ap.de/def/licenses/cc-by-sa/4.0",
  "http://dcat-ap.de/def/licenses/cc-by-sa",
  "http://dcat-ap.de/def/licenses/odcpddl",
  "http://dcat-ap.de/def/licenses/cc-by-de/3.0",
  "http://dcat-ap.de/def/licenses/odbl",
  "http://dcat-ap.de/def/licenses/other-open",
  "https://creativecommons.org/licenses/by/4.0/",
  "http://dcat-ap.de/def/licenses/odby",
  "http://opendatacommons.org/licenses/odbl/",
  "http://dcat-ap.de/def/licenses/dl-by-de/1.0",
  "https://creativecommons.org/licenses/by-nd/4.0",
  "https://w3id.org/mdp/schema/license%23NO_LICENSE"
] as const;

const allFormats = [
  "CSV",
  "XML",
  "XLS",
  "XLSX",
  "WMS_SRVC",
  "ZIP",
  "GEOJSON",
  "Web Page",
  "ArcGIS GeoServices REST API",
  "KML",
  "OGC WFS",
  "JSON",
  "Download",
  "N3",
  "SHP",
  "WFS_SRVC",
  "ATOM",
  "HTML",
  "Information",
  "PDF",
  "TXT",
  "ODS",
  "shape",
  "GPKG",
  "JSON_LD",
  "OCTET",
  "GZIP",
  "Kontakt",
  "ODP",
  "GPX",
  "RSS",
  "7Z",
  "SVG",
  "GEOTIFF",
  "HDF",
  "JPEG",
  "MPEG4",
  "NAS",
  "PNG",
  "REST",
  "TAR",
  "Testdaten"
] as const

export interface InterestingDataset {
  id: string
  title: string
  description: string
  catalog: string
  formats: string[]
  __raw: any
}

export function formatDataset(ds: any): InterestingDataset {
  return {
    id: ds.id || '',
    title: ds.title?.de || '',
    description: ds.description?.de || '',
    catalog: typeof ds.publisher === 'string'
      ? ds.publisher
      : ds.publisher?.name?.de || ds.publisher?.name || '',
    formats: [
      ...new Set(
        ds.distributions?.map((dist: any) => dist?.format?.label || null).filter(Boolean),
      ),
    ] as string[],
    __raw: ds,
  }
}

function createApiInstance(baseUrl: string): AxiosInstance {
  return axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  })
}

function buildSearchUrl(catalogId: string, page: number, limit: number): string {
  const facets = {
    catalog: [catalogId],
    license: allLicenses,
    format: allFormats,
  }
  const includes = 'id,title.de,description.de,publisher,distributions.format.label'
  return `/search?filter=dataset&facets=${encodeURIComponent(JSON.stringify(facets))}&page=${page}&limit=${limit}&includes=${includes}`
}

// Refactored helper functions
interface CatalogMetadata {
  count: number
  page0Datasets: any[]
}

function fetchCatalogMetadata(catalogId: string, api: AxiosInstance) {
  const url = buildSearchUrl(catalogId, 0, 100)
  return from(api.get(url)).pipe(
    map(response => ({
      count: response.data.result.count,
      page0Datasets: response.data.result.results,
    })),
    catchError(error => {
      console.error(`Error fetching metadata for catalog ${catalogId}`, error)
      return of({ count: 0, page0Datasets: [] })
    })
  )
}

// Step 1: Fetch all catalog IDs
function fetchCatalogs(api: AxiosInstance) {
  return from(api.get('/catalogues')).pipe(
    map(response => response.data),
    catchError((error) => {
      console.error('Error fetching catalogs', error)
      return of([]) // Gracefully handle the error
    }),
  )
}

// Step 2: Fetch datasets from a given catalog with pagination
function fetchDatasetByIndex(catalogId: string, api: AxiosInstance, index: number, page0Datasets: any[]) {
  const limit = 100
  const page = Math.floor(index / limit)
  const datasetIndexOnPage = index % limit

  if (page === 0) {
    return of(page0Datasets[datasetIndexOnPage] || null)
  }

  const url = buildSearchUrl(catalogId, page, limit)
  return from(api.get(url)).pipe(
    map(response => response.data.result.results[datasetIndexOnPage] || null),
    catchError(error => {
      console.error(`Error fetching dataset index ${index} from catalog ${catalogId}`, error)
      return of(null)
    })
  )
}

function fetchRandomDatasetFromCatalog(catalogId: string, api: AxiosInstance) {
  return fetchCatalogMetadata(catalogId, api).pipe(
    filter(metadata => metadata.count > 0),
    mergeMap(metadata => {
      const randomIndex = Math.floor(Math.random() * metadata.count)
      return fetchDatasetByIndex(catalogId, api, randomIndex, metadata.page0Datasets)
    }),
    filter(dataset => dataset !== null),
    map(formatDataset)
  )
}

// Step 4: Select three random valid catalogs and one random dataset from each
function selectRandomDatasets(baseUrl: string) {
  const api = createApiInstance(baseUrl)

  return fetchCatalogs(api).pipe(
    mergeMap((catalogIds: string[]) => {
      const blacklist = ['wendelstein', 'wendelstein-subcatalog']
      const filteredIds = catalogIds.filter(id =>
        !blacklist.includes(id.trim().toLowerCase())
      )
      const shuffledIds = [...filteredIds].sort(() => Math.random() - 0.5)

      return from(shuffledIds).pipe(
        mergeMap(catalogId =>
          fetchRandomDatasetFromCatalog(catalogId, api),
          3 // Concurrent requests
        ),
        take(3),
        toArray()
      )
    })
  )
}

// Step 5: Execute the selection process and log results
export function fetchThreeRandomDatsets(baseUrl: string) {
  // return observable as promise
  return new Promise<InterestingDataset[]>((resolve, reject) => {
    selectRandomDatasets(baseUrl).subscribe({
      next: results => resolve(results),
      error: err => reject(err),
    })
  })
}


export function fetchThreeRandomDatasetsByCatalogId(baseUrl: string, catalogId: string) {
  const api = createApiInstance(baseUrl)

  return fetchCatalogMetadata(catalogId, api).pipe(
    filter(metadata => metadata.count >= 1),
    mergeMap(metadata => {
      // Generate three random indices (allow duplicates if count < 3)
      const indices = Array.from({ length: 3 }, () =>
        Math.floor(Math.random() * metadata.count)
      )

      return from(indices).pipe(
        mergeMap(index =>
          fetchDatasetByIndex(catalogId, api, index, metadata.page0Datasets).pipe(
            filter(dataset => dataset !== null),
            map(formatDataset)
          )
        ),
        take(3),
        toArray()
      )
    }),
    catchError(error => {
      console.error(`Error fetching datasets from catalog ${catalogId}`, error)
      return of([])
    })
  )
}

export function fetchThreeRandomDatasetsByCatalogIdAsPromise(baseUrl: string, catalogId: string) {
  return new Promise<InterestingDataset[]>((resolve, reject) => {
    fetchThreeRandomDatasetsByCatalogId(baseUrl, catalogId).subscribe({
      next: resolve,
      error: reject
    })
  })
}
